import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, Award, Download, Search, Filter,
  Check, Loader2, Users, FileText, Calendar,
  X, Printer, Star
} from 'lucide-react';
import { useCertificates, useCertificateEligibility, useIssueCertificate } from '../../hooks/useTeacher';
import toast from 'react-hot-toast';

const AVATAR_EMOJIS = {
  boy: '👦',
  girl: '👧',
  seedling: '🌱',
  tree: '🌳',
  flower: '🌻',
  bee: '🐝',
};

const TABS = [
  { id: 'eligible', label: 'Eligible Students', icon: Users },
  { id: 'issued', label: 'Issued Certificates', icon: FileText },
];

export default function Certificates() {
  const { data: certificatesData, isLoading: certsLoading } = useCertificates();
  const { data: eligibilityData, isLoading: eligibilityLoading } = useCertificateEligibility();
  const issueCertificate = useIssueCertificate();

  const [activeTab, setActiveTab] = useState('eligible');
  const [searchQuery, setSearchQuery] = useState('');
  const [showPreview, setShowPreview] = useState(null);
  const [issuingId, setIssuingId] = useState(null);

  const certificates = certificatesData?.certificates || [];
  const eligibleStudents = eligibilityData?.students || [];

  const isLoading = certsLoading || eligibilityLoading;

  // Filter students with eligible certificates
  const studentsWithEligible = eligibleStudents.filter(
    (s) => s.eligible_certificates.length > 0
  );

  // Filter by search
  const filteredEligible = studentsWithEligible.filter((student) =>
    student.student_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredCertificates = certificates.filter((cert) =>
    cert.student_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cert.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleIssueCertificate = async (studentId, certificate) => {
    setIssuingId(`${studentId}-${certificate.title}`);
    try {
      await issueCertificate.mutateAsync({
        student_id: studentId,
        type: certificate.type,
        title: certificate.title,
        data: certificate,
      });
    } finally {
      setIssuingId(null);
    }
  };

  const handleDownloadCertificate = (cert) => {
    // Generate certificate HTML for printing
    const certHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${cert.title}</title>
        <style>
          @page { size: landscape; margin: 0; }
          body {
            font-family: 'Georgia', serif;
            margin: 0;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
          }
          .certificate {
            width: 900px;
            padding: 60px;
            background: white;
            border: 8px double #2e7d32;
            border-radius: 20px;
            text-align: center;
            box-shadow: 0 20px 60px rgba(0,0,0,0.1);
          }
          .header {
            font-size: 18px;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 3px;
            margin-bottom: 20px;
          }
          .title {
            font-size: 48px;
            color: #2e7d32;
            margin: 20px 0;
            font-weight: bold;
          }
          .subtitle {
            font-size: 24px;
            color: #444;
            margin: 10px 0;
          }
          .name {
            font-size: 36px;
            color: #1b5e20;
            margin: 40px 0;
            font-style: italic;
            border-bottom: 2px solid #4caf50;
            display: inline-block;
            padding: 0 40px 10px;
          }
          .description {
            font-size: 18px;
            color: #666;
            margin: 30px 0;
            line-height: 1.6;
          }
          .date {
            font-size: 16px;
            color: #888;
            margin-top: 40px;
          }
          .logo {
            font-size: 60px;
            margin-bottom: 20px;
          }
        </style>
      </head>
      <body>
        <div class="certificate">
          <div class="logo">🌱</div>
          <div class="header">Green Clubs Program</div>
          <div class="title">${cert.title}</div>
          <div class="subtitle">This is to certify that</div>
          <div class="name">${cert.student_name}</div>
          <div class="description">
            Has successfully completed all requirements and demonstrated<br>
            outstanding commitment to environmental stewardship.
          </div>
          <div class="date">Issued on ${new Date(cert.issued_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}</div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(certHtml);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const getCertificateIcon = (type) => {
    switch (type) {
      case 'monthly':
        return '📜';
      case 'term':
        return '🏆';
      case 'annual':
        return '🎖️';
      default:
        return '📜';
    }
  };

  const getCertificateColor = (type) => {
    switch (type) {
      case 'monthly':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'term':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'annual':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-leaf-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading certificates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Link
              to="/teacher"
              className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <ChevronLeft size={20} className="text-gray-600" />
            </Link>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Certificates</h1>
              <p className="text-gray-600">Issue and manage student certificates</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm p-5"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-leaf-100 flex items-center justify-center">
                <Users size={20} className="text-leaf-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{studentsWithEligible.length}</p>
                <p className="text-sm text-gray-500">Students Ready for Certificates</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-sm p-5"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Award size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{certificates.length}</p>
                <p className="text-sm text-gray-500">Certificates Issued</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-sm p-5"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-sunshine-100 flex items-center justify-center">
                <Star size={20} className="text-sunshine-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {studentsWithEligible.reduce((sum, s) => sum + s.eligible_certificates.length, 0)}
                </p>
                <p className="text-sm text-gray-500">Pending Certificates</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-leaf-700 shadow'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative max-w-md mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search students or certificates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-leaf-500 focus:ring-2 focus:ring-leaf-200 transition-all"
          />
        </div>

        {/* Content */}
        {activeTab === 'eligible' ? (
          <div className="space-y-4">
            {filteredEligible.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <Award size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-2">No students eligible for certificates yet</p>
                <p className="text-sm text-gray-400">Students become eligible when they complete months, terms, or the full year</p>
              </div>
            ) : (
              filteredEligible.map((student, index) => (
                <motion.div
                  key={student.student_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl shadow-sm overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-full bg-leaf-100 flex items-center justify-center text-2xl">
                        {AVATAR_EMOJIS[student.avatar] || '🌱'}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{student.student_name}</h3>
                        <p className="text-sm text-gray-500">
                          {student.eligible_certificates.length} certificate{student.eligible_certificates.length > 1 ? 's' : ''} ready to issue
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {student.eligible_certificates.map((cert) => (
                        <div
                          key={cert.title}
                          className={`flex items-center justify-between p-4 rounded-lg border ${getCertificateColor(cert.type)}`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{getCertificateIcon(cert.type)}</span>
                            <div>
                              <p className="font-medium">{cert.title}</p>
                              <p className="text-sm opacity-75 capitalize">{cert.type} Certificate</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleIssueCertificate(student.student_id, cert)}
                            disabled={issuingId === `${student.student_id}-${cert.title}`}
                            className="btn-primary py-2 px-4 flex items-center gap-2"
                          >
                            {issuingId === `${student.student_id}-${cert.title}` ? (
                              <Loader2 size={18} className="animate-spin" />
                            ) : (
                              <>
                                <Award size={18} />
                                Issue
                              </>
                            )}
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Already earned certificates */}
                    {student.earned_certificates.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-sm text-gray-500 mb-2">Already earned:</p>
                        <div className="flex flex-wrap gap-2">
                          {student.earned_certificates.map((cert) => (
                            <span
                              key={cert.id}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm"
                            >
                              <Check size={14} className="text-leaf-500" />
                              {cert.title}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {filteredCertificates.length === 0 ? (
              <div className="p-12 text-center">
                <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No certificates have been issued yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredCertificates.map((cert, index) => (
                  <motion.div
                    key={cert.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-4 flex items-center gap-4 hover:bg-gray-50"
                  >
                    <span className="text-3xl">{getCertificateIcon(cert.type)}</span>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{cert.title}</p>
                      <p className="text-sm text-gray-500">
                        Issued to <span className="font-medium">{cert.student_name}</span>
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-sm text-gray-400 mb-1">
                        <Calendar size={14} />
                        {new Date(cert.issued_at).toLocaleDateString()}
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getCertificateColor(cert.type)}`}>
                        {cert.type}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleDownloadCertificate(cert)}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                        title="Print Certificate"
                      >
                        <Printer size={18} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
